import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyReportApi } from '../api/surveyReportApi';
import { useNotifications } from '@shared/components/notifications';
import { surveyReportQueryKeys } from './useSurveyReportsForDataManagement';

// Create data management hooks for all tenant reports
export const useAllTenantReportsDataManagement = (tenantId: string) => {
  const { success, error } = useNotifications();
  const queryClient = useQueryClient();

  // Query hook for getting all reports by tenant
  const useAllTenantReportsQuery = () => {
    return useQuery({
      queryKey: surveyReportQueryKeys.byTenant(tenantId),
      queryFn: async () => {
        const response = await surveyReportApi.getReportsByTenantId(tenantId);

        // Handle case where server returns success message instead of data
        if (response.data && typeof response.data === 'object' && 'success' in response.data) {
          return [];
        }

        // Return the reports array directly
        return response.data?.reports || [];
      },
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: true,
    });
  };

  // Delete mutation wrapper (admins can delete any report in their tenant)
  const useDeleteSurveyReportWrapper = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        await surveyReportApi.delete(id);
        return id;
      },
      onSuccess: (deletedId) => {
        // Invalidate and refetch reports
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.byTenant(tenantId),
        });
        // Also invalidate all subject queries as the report might belong to any subject
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.all,
        });
        // Remove from cache
        queryClient.removeQueries({
          queryKey: surveyReportQueryKeys.detail(deletedId),
        });
        success('Report deleted successfully');
      },
      onError: (err: any) => {
        error(`Failed to delete report: ${err.message}`);
      },
    });
  };

  return {
    useAllTenantReportsQuery,
    useDeleteSurveyReportWrapper,
  };
};
